import type { FocusEvent } from "react";

// macOS hides an overlay scrollbar until it is used, leaving nothing to say a region scrolls sideways.
export const alwaysVisibleScrollbar = {
  "&::-webkit-scrollbar": { height: "8px" },
  "&::-webkit-scrollbar-track": { bg: "smBackgroundSecondary", borderRadius: "full" },
  "&::-webkit-scrollbar-thumb": { bg: "smBackgroundTertiary", borderRadius: "full" },
} as const;

// typeof guard: jsdom has no matchMedia
export const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const revealColumn = (column: HTMLElement) => {
  // Next frame: the browser scrolls the focused control just into view first, and ours must run after that.
  requestAnimationFrame(() =>
    column.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    }),
  );
};

// scroll-margin widens what scrollIntoView counts as visible, so a revealed column keeps a gap.
export const revealedColumnProps = () => ({
  sx: { scrollMarginInline: "1rem" },
  onFocus: (event: FocusEvent<HTMLElement>) => revealColumn(event.currentTarget),
});

export const revealSection = (section: HTMLElement) => {
  section.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
  section.querySelector<HTMLElement>("h2, h3, h4, [tabindex]")?.focus();
};

export const alignItemToRowStart = (row: HTMLElement, item: HTMLElement) => {
  row.scrollLeft += item.getBoundingClientRect().left - row.getBoundingClientRect().left;
};

export const revealTabInRow = (row: HTMLElement, index: number) => {
  const tab = row.children[index];
  if (!tab) return;
  const rowBox = row.getBoundingClientRect();
  const tabBox = tab.getBoundingClientRect();
  if (tabBox.left < rowBox.left) row.scrollLeft -= rowBox.left - tabBox.left;
  else if (tabBox.right > rowBox.right) row.scrollLeft += tabBox.right - rowBox.right;
};
